import { optional, z } from 'zod'

export const EntityItemSchema = z.object({
  identifier: z.string().min(1),
  id: z.string().min(1),
})

export const IdentifierOnlyEntityItemSchema = z.object({
  identifier: z.string().min(1),
})

export const OrganizationItemSchema = EntityItemSchema.extend({
  role: z.string().min(1),
  ref: z.string().optional(),
})

export type OrganizationItemType = z.infer<typeof OrganizationItemSchema>

export const OrganizationSchema = z.array(OrganizationItemSchema).optional()
export type OrganizationType = z.infer<typeof OrganizationSchema>

export const EntityItemArraySchema = z.array(EntityItemSchema)
export type EntityItemArrayType = z.infer<typeof EntityItemArraySchema>

export const EntityItemArrayOptionalSchema = EntityItemArraySchema.optional()
export type EntityItemArrayOptionalSchemaType = z.infer<
  typeof EntityItemArrayOptionalSchema
>

export const OptionalStringSchema = z.string().optional()
export const OptionalNumberSchema = z.number().optional()

export const LocationItemSchema = EntityItemSchema.extend({
  isApproximate: z.boolean().optional(),
})
export type LocationItemType = z.infer<typeof LocationItemSchema>
export const LocationItemArraySchema = z.array(LocationItemSchema)

export const YYYYMMDDRegex = /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/
export const YYYYMMDDRegexSchema = z
  .string()
  .refine((value) => YYYYMMDDRegex.test(value), {
    message: 'Invalid date format',
  })
export const YYYYMMDDRegexOptionalSchema =
  YYYYMMDDRegexSchema.optional().nullable()

const FlexibleDateRegex =
  /^(\d{4}-\d{2}-\d{2})(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z)?$/

export const FlexibleDateRegexSchema = z
  .string()
  .refine((value) => FlexibleDateRegex.test(value), {
    message: 'Invalid date format. Use YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss.sssZ',
  })

export const FlexibleDateRegexOptionalSchema =
  FlexibleDateRegexSchema.optional().nullable()

const ISODateTimeWithoutZSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/)

export const ISODateTimeSchema = z.union([
  z.string().datetime(),
  ISODateTimeWithoutZSchema,
  FlexibleDateRegexSchema,
])

export const ISODateTimeOptionalSchema = z
  .union([
    z.string().datetime(),
    ISODateTimeWithoutZSchema,
    FlexibleDateRegexSchema,
  ])
  .optional()

export const QueueJoinSchema = z
  .object({
    queueKey: OptionalStringSchema,
    applicationId: OptionalStringSchema,
    queueDataset: OptionalStringSchema,
    id: z.string(),
    identifier: z.string(),
  })
  .array()

export const QueueJoinOptionalSchema = QueueJoinSchema.optional()
export type QueueJoinOptionalSchemaType = z.infer<
  typeof QueueJoinOptionalSchema
>

export const LinkedPeopleSchema = z
  .object({
    id: OptionalStringSchema,
    identifier: OptionalStringSchema,
    linkedinUrl: optional(z.string().url()),
    role: OptionalStringSchema,
    fullName: OptionalStringSchema,
    email: OptionalStringSchema,
    phone: OptionalStringSchema,
    source: OptionalStringSchema,
  })
  .array()

export const LinkedPeopleOptionalSchema = LinkedPeopleSchema.optional()
export type LinkedPeopleOptionalSchemaType = z.infer<
  typeof LinkedPeopleOptionalSchema
>

export const AddressSchema = z.object({
  full: OptionalStringSchema,
  city: OptionalStringSchema,
  state: OptionalStringSchema,
  street: OptionalStringSchema,
  street2: OptionalStringSchema,
  zip: OptionalStringSchema,
  country: OptionalStringSchema,
  latitude: OptionalNumberSchema,
  longitude: OptionalNumberSchema,
})

export const OptionalAddressSchema = AddressSchema.optional()
export type OptionalAddressType = z.infer<typeof OptionalAddressSchema>

export const CoordinateSchema = z.object({ lat: z.number(), lon: z.number() })
export const OptionalCoordinateSchema = CoordinateSchema.optional()

export const DocumentS3RecordSchema = z
  .object({
    filename: z.string(),
    created: YYYYMMDDRegexSchema,
    document_id: z.string(),
    mimetype: z.string(),
    scanResultStatus: z.string().optional(),
    type: z.string().array().optional(),
    key: OptionalStringSchema,
    userName: OptionalStringSchema,
  })
  .array()

export const OptionalS3DocumentSchema = DocumentS3RecordSchema.optional()
export type OptionalS3DocumentType = z.infer<typeof OptionalS3DocumentSchema>

export const DocumentLinkSchema = z
  .object({
    file: OptionalStringSchema,
    url: z.string().url(),
    created: OptionalStringSchema,
    type: z.array(z.string()),
    scanResultStatus: z.string().optional(),
    documentDate: YYYYMMDDRegexSchema,
    source: OptionalStringSchema,
    id: OptionalStringSchema,
  })
  .array()

export const OptionalDocumentLinkSchema = DocumentLinkSchema.optional()
export type OptionalDocumentLinkType = z.infer<
  typeof OptionalDocumentLinkSchema
>

export const DocumentRecordSchema = z
  .union([DocumentS3RecordSchema, DocumentLinkSchema])
  .optional()
export type DocumentRecordType = z.infer<typeof DocumentRecordSchema>
export type DocumentS3RecordType = z.infer<
  typeof DocumentS3RecordSchema
>[number]

export const defaultValue = z.string()
export const geoPoint = z.object({
  lon: z.number(),
  lat: z.number(),
})
export const GeoSchema = z
  .object({
    defaultValue: z.string(),
    default: geoPoint,
    project: geoPoint,
    county: geoPoint,
    facility: geoPoint,
  })
  .partial()

export type GeoType = z.infer<typeof GeoSchema>

export const TotalsSchema = z
  .object({
    offtaker: z.number().optional(),
    projects: z.number().optional(),
    keyPeople: z.number().optional(),
    documents: z.number().optional(),
    organizations: z.number().optional(),
    queueApplications: z.number().optional(),
    utility: z.number().optional(),
    puc: z.number().optional(),
    epc: z.number().optional(),
    developer: z.number().optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: 'At least one property must be present if totals object exists',
  })
  .optional()

const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/

export const FingerprintSchema = z
  .string()
  .regex(base64Regex, 'Invalid Base64 string')
  .min(1, 'Fingerprint cannot be empty')
